import { Controller, useFormState, useWatch } from 'react-hook-form';
import {
  Clone,
  DatePicker,
  Delete,
  Warning,
  TimeWindowControl,
} from 'components';
import { CircleButton } from 'components/CircleButton';
import { StaticWeightedTimeWindow } from 'types/StaticWeighted';
import { useFormErrors } from 'hooks';

type Props = {
  timeWindow: StaticWeightedTimeWindow;
  index: number;
  isOpened: boolean;
  onClone: Function;
  onRemove: Function;
  refreshDates: Function;
  control: any;
};

export const StaticWeightedTimeWindowHeader = ({
  isOpened,
  onClone,
  onRemove,
  index,
  refreshDates,
  control,
}: Props) => {
  const { errors } = useFormState({ control, name: `timeWindows[${index}]` });
  const timeWindows = useWatch({ name: 'timeWindows', control });
  const fieldName = `timeWindows[${index}]`;
  const { getErrorMessage, hasErrors } = useFormErrors({
    errors,
    fieldName,
    index,
  });

  return (
    <div
      key={index}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        paddingRight: '20px',
      }}
    >
      <div
        style={{
          maxWidth: '90%',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '10px',
          paddingBottom: '6px',
        }}
      >
        <TimeWindowControl label='From Date'>
          <Controller
            name={`${fieldName}.fromDate`}
            control={control}
            render={({ field: { onChange, value } }) => {
              const dateValue =
                typeof value === 'string' ? new Date(value) : value;

              return (
                <DatePicker
                  key='fromDate'
                  testid={`${fieldName}.fromDate`}
                  onChange={onChange}
                  onCalendarClose={() => refreshDates()}
                  selected={dateValue}
                  errorMessage={getErrorMessage(`${fieldName}.fromDate`)}
                  style={{ width: 120 }}
                  size='sm'
                />
              );
            }}
          />
        </TimeWindowControl>
        <TimeWindowControl label='To Date'>
          <Controller
            control={control}
            name={`${fieldName}.toDate`}
            render={({ field: { onChange, value } }) => {
              return (
                <DatePicker
                  key='toDate'
                  testid={`${fieldName}.toDate`}
                  onChange={onChange}
                  selected={value}
                  disabled
                  hasError={getErrorMessage(`${fieldName}.toDate`)}
                  style={{ width: 120, color: '#8795A2' }}
                  size='sm'
                />
              );
            }}
          />
        </TimeWindowControl>
      </div>
      <div
        style={{
          flex: '0 1 auto',
          alignSelf: 'center',
          justifyContent: 'end',
          display: 'flex',
          flexDirection: 'row',
          width: '200px',
        }}
      >
        {isOpened && onClone && (
          <CircleButton
            key='clone'
            data-testid={`${fieldName}-clone`}
            icon={<Clone size='7px' />}
            title='Clone'
            onClick={() => {
              onClone({ ...timeWindows[index] });
            }}
          />
        )}
        {isOpened && onRemove && (
          <CircleButton
            key='delete'
            disabled={timeWindows.length === 1}
            data-testid={`${fieldName}-delete`}
            icon={<Delete size='7px' />}
            title='Delete'
            onClick={() => {
              onRemove(index);
            }}
          />
        )}
        {!isOpened && hasErrors && (
          <Warning size='13px' className='m-1' fill='#AA0101' />
        )}
      </div>
    </div>
  );
};
